/**
 * @fileoverview Report attachments panel (V1)
 *
 * Purpose: Let users upload a single file per "attachment" section (annexure)
 * of a generated report, directly in the report generator. Uploaded files are
 * stored in Convex storage, surfaced here with a download link, and bundled
 * into report exports.
 *
 * Upload flow reuses the shared gather-phase pattern: `generateUploadUrl` →
 * POST the file to storage → record the storage ref on the section via
 * `attachSectionFile`.
 *
 * Used by: ReportingView (report review / complete view).
 */

import { useRef, useState } from "react";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { Button } from "@/ui/primitives";
import {
  ChevronDown,
  Download,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";

/** One attachment section as surfaced by `getReport`. */
export interface AttachmentSection {
  _id: Id<"reportSections">;
  title: string;
  attachmentFileName?: string;
  attachmentFileSize?: number;
  attachmentUrl?: string | null;
}

/** Human-readable file size, e.g. "1.2 MB". */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || Number.isInteger(value) ? 0 : 1)} ${units[unit]}`;
}

/**
 * Collapsible list of a report's annexures with per-annexure upload / replace /
 * remove / download. Renders nothing when the report has no attachment
 * sections.
 *
 * @component
 * @example
 * <ReportAttachmentsPanel jobId={jobId} attachments={attachmentSections} />
 */
export function ReportAttachmentsPanel({
  jobId,
  attachments,
}: {
  jobId: Id<"jobs">;
  attachments: AttachmentSection[];
}) {
  const [open, setOpen] = useState(true);
  const [busyId, setBusyId] = useState<Id<"reportSections"> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingSectionRef = useRef<Id<"reportSections"> | null>(null);

  const generateUploadUrl = useConvexMutation(
    api.jobs.gatherInformation.documents.mutations.generateUploadUrl,
  );
  const attachSectionFile = useConvexMutation(
    api.jobs.reporting.mutations.attachSectionFile,
  );
  const removeSectionAttachment = useConvexMutation(
    api.jobs.reporting.mutations.removeSectionAttachment,
  );

  if (attachments.length === 0) return null;

  const filledCount = attachments.filter((a) => a.attachmentFileName).length;

  const pickFileFor = (sectionId: Id<"reportSections">) => {
    pendingSectionRef.current = sectionId;
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (file: File) => {
    const sectionId = pendingSectionRef.current;
    pendingSectionRef.current = null;
    if (!sectionId) return;
    setBusyId(sectionId);
    try {
      const contentType = file.type || "application/octet-stream";
      const uploadUrl = await generateUploadUrl({ jobId });
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": contentType },
        body: file,
      });
      if (!response.ok) throw new Error("Upload to storage failed");
      const { storageId } = (await response.json()) as {
        storageId: Id<"_storage">;
      };
      await attachSectionFile({
        sectionId,
        storageId,
        fileName: file.name,
        fileType: contentType,
        fileSize: file.size,
      });
      toast.success(`Attached ${file.name}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to attach file"));
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (sectionId: Id<"reportSections">) => {
    setBusyId(sectionId);
    try {
      await removeSectionAttachment({ sectionId });
      toast.success("Attachment removed");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to remove attachment"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-3 shrink-0 rounded-xl border bg-background">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so choosing the same file again still fires onChange.
          e.target.value = "";
          if (file) void handleFileChosen(file);
        }}
      />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium"
      >
        <Paperclip className="size-4 text-muted-foreground" />
        <span>Attachments</span>
        <span className="text-xs font-normal text-muted-foreground">
          {filledCount} of {attachments.length} uploaded
        </span>
        <ChevronDown
          className={cn(
            "ml-auto size-4 text-muted-foreground transition-transform",
            open ? "rotate-180" : "",
          )}
        />
      </button>
      {open && (
        <ul className="divide-y border-t">
          {attachments.map((section) => {
            const busy = busyId === section._id;
            const hasFile = Boolean(section.attachmentFileName);
            return (
              <li
                key={section._id}
                className="flex items-center gap-3 px-4 py-2.5 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{section.title}</div>
                  {hasFile ? (
                    <div className="truncate text-xs text-muted-foreground">
                      {section.attachmentFileName}
                      {section.attachmentFileSize
                        ? ` · ${formatBytes(section.attachmentFileSize)}`
                        : ""}
                    </div>
                  ) : (
                    <div className="text-xs italic text-muted-foreground">
                      No file attached
                    </div>
                  )}
                </div>
                {busy ? (
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                ) : hasFile ? (
                  <div className="flex items-center gap-1">
                    {section.attachmentUrl && (
                      <Button asChild size="sm" variant="ghost">
                        <a
                          href={section.attachmentUrl}
                          target="_blank"
                          rel="noreferrer"
                          download={section.attachmentFileName}
                        >
                          <Download className="mr-1 size-3.5" />
                          Download
                        </a>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => pickFileFor(section._id)}
                    >
                      <Upload className="mr-1 size-3.5" />
                      Replace
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void handleRemove(section._id)}
                    >
                      <Trash2 className="size-3.5 text-destructive" />
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" onClick={() => pickFileFor(section._id)}>
                    <Upload className="mr-1 size-3.5" />
                    Upload
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
